import { useState, useEffect, useCallback } from "react";
import { API_BASE_URL } from "../environment";
import { cachedRequest } from "../utils/requestCache";
import {
  CustomField,
  CustomFieldOption,
  CustomFieldDependencyLevel,
} from "./useCompanyCustomFields";

interface UseCustomFieldsReturn {
  customFields: CustomField[];
  loading: boolean;
  error: string | null;
  fetchCustomFields: () => Promise<void>;
  fetchFieldOptions: (customFieldId: number) => Promise<CustomFieldOption[]>;
  fetchDependencyLevels: (customFieldId: number) => Promise<CustomFieldDependencyLevel[]>;
}

export const useCustomFields = (
  tenantId: string | null,
  entityName: string
): UseCustomFieldsReturn => {
  const [customFields, setCustomFields] = useState<CustomField[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getAuthHeaders = () => {
    const authHeader = localStorage.getItem("authCred");
    if (!authHeader) {
      throw new Error("No authentication credentials found");
    }
    return {
      "Content-Type": "application/json",
      Authorization: `Basic ${authHeader}`,
    };
  };

  const fetchCustomFields = useCallback(async () => {
    if (!tenantId) return;

    setLoading(true);
    setError(null);

    try {
      const sortedFields = await cachedRequest(`custom-fields:${tenantId}:${entityName}`, async () => {
        const response = await fetch(
          `${API_BASE_URL}/custom-field/?tenant_id=${tenantId}&related_entity_name=${entityName}`,
          {
            method: "GET",
            headers: getAuthHeaders(),
          }
        );

        if (!response.ok) {
          throw new Error(`Failed to fetch custom fields: ${response.status}`);
        }

        const data = await response.json();
        const fields = data.results || data.data || [];

        return fields
          .filter((field: CustomField) => field.is_active)
          .sort(
            (a: CustomField, b: CustomField) => a.display_order - b.display_order
          );
      });

      setCustomFields(sortedFields);
    } catch (err) {
      console.error("Error fetching custom fields:", err);
      setError(
        err instanceof Error ? err.message : "Failed to fetch custom fields"
      );
    } finally {
      setLoading(false);
    }
  }, [tenantId, entityName]);

  const fetchFieldOptions = useCallback(
    async (customFieldId: number): Promise<CustomFieldOption[]> => {
      try {
        return await cachedRequest(`custom-field-choice-option:${customFieldId}`, async () => {
          const response = await fetch(
            `${API_BASE_URL}/custom-field-choice-option/?custom_field_id=${customFieldId}&response_mode=light`,
            {
              method: "GET",
              headers: getAuthHeaders(),
            }
          );

          if (!response.ok) {
            throw new Error(`Failed to fetch field options: ${response.status}`);
          }

          const data = await response.json();
          const options = data.results || data.data || [];

          return options.sort(
            (a: CustomFieldOption, b: CustomFieldOption) =>
              a.display_order - b.display_order
          );
        });
      } catch (err) {
        console.error("Error fetching field options:", err);
        return [];
      }
    },
    []
  );

  const fetchDependencyLevels = useCallback(
    async (customFieldId: number): Promise<CustomFieldDependencyLevel[]> => {
      try {
        return await cachedRequest(`custom-field-dependency-level:${customFieldId}`, async () => {
          const response = await fetch(
            `${API_BASE_URL}/custom-field-dependency-level/?custom_field_id=${customFieldId}`,
            {
              method: "GET",
              headers: getAuthHeaders(),
            }
          );

          if (!response.ok) {
            throw new Error(`Failed to fetch dependency levels: ${response.status}`);
          }

          const data = await response.json();
          const levels = data.results || data.data || [];

          return levels.sort(
            (a: CustomFieldDependencyLevel, b: CustomFieldDependencyLevel) =>
              a.display_order - b.display_order
          );
        });
      } catch (err) {
        console.error("Error fetching dependency levels:", err);
        return [];
      }
    },
    []
  );

  useEffect(() => {
    fetchCustomFields();
  }, [fetchCustomFields]);

  return {
    customFields,
    loading,
    error,
    fetchCustomFields,
    fetchFieldOptions,
    fetchDependencyLevels,
  };
};

